import type { ThemeConfig } from 'antd';
import { theme } from 'antd';
import { COMPONENT_SIZE, Z_INDEX } from 'theme/constants.ts';
import { darkColors, lightColors } from './colors';

export const darkThemeConfig: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    // Global look & feel
    colorPrimary: darkColors.primary,
    colorSuccess: darkColors.success,
    colorWarning: darkColors.warning,
    colorError: darkColors.error,
    colorInfo: darkColors.info,

    colorBgBase: darkColors.bgBase,
    colorBgContainer: darkColors.bgElevated,
    colorBgElevated: darkColors.bgElevated,
    colorBorder: darkColors.border,
    colorSplit: darkColors.border,

    colorTextBase: darkColors.text,
    colorText: darkColors.text,
    colorTextSecondary: darkColors.textSec,
    colorTextTertiary: darkColors.textTertiary,
    colorTextQuaternary: darkColors.textDisabled,

    controlItemBgHover: darkColors.hover,
    controlItemBgActive: darkColors.selected,

    // Border radius (modern 8px default)
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,

    // Control heights (44px minimum for accessibility)
    controlHeight: COMPONENT_SIZE.controlHeight,
    controlHeightLG: COMPONENT_SIZE.controlHeightLG,
    controlHeightSM: COMPONENT_SIZE.controlHeightSM,

    // Padding
    padding: 16,
    paddingSM: 12,
    paddingLG: 24,

    // Typography
    fontSize: 14,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

    // Shadows (simplified per new spec)
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
    boxShadowSecondary: '0 2px 8px rgba(0, 0, 0, 0.5)',

    // Link color aligns with primary
    colorLink: darkColors.primary,
    colorLinkHover: darkColors.primaryHover,
    colorLinkActive: darkColors.primaryActive,

    // Pin antd popups/modals above the sticky header (--z-sticky = 1020).
    // Antd's default of 1000 lets the header bleed through the backdrop.
    zIndexPopupBase: Z_INDEX.zIndexModal,
  },

  components: {
    Layout: {
      headerBg: darkColors.bgElevated,
      siderBg: darkColors.bgElevated,
      bodyBg: darkColors.bgBase,
      headerHeight: 64,
    },

    Card: {
      colorBgContainer: darkColors.bgElevated,
      boxShadowTertiary: darkColors.shadowCard,
      headerHeight: 52,
      padding: 20,
      borderRadiusLG: 12,
    },

    Button: {
      borderRadius: 8,
      controlHeight: 44,
      paddingInline: 24,
      colorPrimary: darkColors.primary,
      colorPrimaryHover: darkColors.primaryHover,
      colorPrimaryActive: darkColors.primaryActive,
      primaryShadow: darkColors.shadowFocus,
      defaultColor: darkColors.text,
      defaultBg: darkColors.bgControl,
      defaultBorderColor: darkColors.border,
      defaultHoverBg: darkColors.hover,
      defaultActiveBg: darkColors.selected,
      defaultShadow: darkColors.shadowSubtle,
      ghostBg: 'transparent',
    },

    Input: {
      controlHeight: 44,
      colorBgContainer: darkColors.bgControl,
      colorTextPlaceholder: darkColors.textTertiary,
      colorBorder: darkColors.border,
      activeBorderColor: darkColors.primary,
      activeShadow: darkColors.shadowFocus,
      hoverBorderColor: darkColors.primary,
      borderRadius: 8,
      paddingInline: 16,
      addonBg: darkColors.bgSunken,
    },

    Select: {
      controlHeight: 44,
      colorBgContainer: darkColors.bgControl,
      colorBorder: darkColors.border,
      activeBorderColor: darkColors.primary,
      colorTextPlaceholder: darkColors.textTertiary,
      optionSelectedBg: darkColors.selected,
      optionActiveBg: darkColors.hover,
      borderRadius: 8,
    },

    DatePicker: {
      controlHeight: 44,
      colorBgContainer: darkColors.bgControl,
      colorBorder: darkColors.border,
      activeBorderColor: darkColors.primary,
      cellHoverWithRangeBg: darkColors.hover,
      colorBgElevated: darkColors.bgElevated,
    },

    Checkbox: {
      borderRadiusSM: 6,
      colorBgContainer: darkColors.bgControl,
      colorBorder: darkColors.border,
      colorPrimary: darkColors.primary,
    },

    Radio: {
      colorPrimary: darkColors.primary,
      buttonCheckedBg: darkColors.selected,
      buttonSolidCheckedBg: darkColors.primary,
    },

    Switch: {
      colorPrimary: darkColors.primary,
      colorBgContainer: darkColors.bgSunken,
      trackHeight: 22,
      handleSize: 18,
      borderRadiusSM: 20,
    },

    Tag: {
      colorText: darkColors.text,
      defaultBg: darkColors.selected,
      borderRadiusSM: 6,
      colorSuccess: darkColors.success,
      colorError: darkColors.error,
      colorWarning: darkColors.warning,
      colorInfo: darkColors.info,
    },

    Badge: {
      colorBorderBg: darkColors.bgElevated,
      colorSuccess: darkColors.success,
      colorError: darkColors.error,
      colorWarning: darkColors.warning,
      colorInfo: darkColors.info,
    },

    Table: {
      colorBgContainer: darkColors.bgElevated,
      colorFillAlter: darkColors.surfaceSecondary,
      headerBg: darkColors.bgElevated,
      headerColor: darkColors.textSec,
      borderColor: darkColors.border,
      rowHoverBg: darkColors.hover,
      rowSelectedBg: darkColors.selected,
      rowSelectedHoverBg: darkColors.selected,
      stickyScrollBarBg: darkColors.surfaceTertiary,
      stickyScrollBarBorderRadius: 6,
      cellPaddingBlock: 16,
      cellPaddingInline: 12,
      fontSize: 14,
    },

    Pagination: {
      itemActiveBg: darkColors.selected,
      colorText: darkColors.text,
      colorBgContainer: darkColors.bgControl,
      colorBorder: darkColors.border,
    },

    Tabs: {
      itemActiveColor: darkColors.text,
      itemSelectedColor: darkColors.text,
      itemHoverColor: darkColors.textSec,
      inkBarColor: darkColors.primary,
      cardBg: darkColors.bgElevated,
      titleFontSizeLG: 16,
    },

    Menu: {
      darkItemBg: darkColors.bgElevated,
      darkSubMenuItemBg: darkColors.bgSunken,
      darkItemSelectedBg: darkColors.selected,
      darkItemColor: darkColors.textSec,
      darkItemSelectedColor: darkColors.text,
      itemBorderRadius: 8,
      itemBg: darkColors.bgElevated,
      subMenuItemBg: darkColors.bgSunken,
      itemSelectedBg: darkColors.selected,
      itemColor: darkColors.textSec,
      itemSelectedColor: darkColors.text,
    },

    Dropdown: {
      colorBgElevated: darkColors.bgElevated,
      controlItemBgHover: darkColors.hover,
      controlItemBgActive: darkColors.selected,
      colorBorder: darkColors.border,
      boxShadowSecondary: darkColors.shadowElevated,
    },

    Popover: {
      colorBgElevated: darkColors.bgElevated,
      colorText: darkColors.text,
      colorBorder: darkColors.border,
    },

    Tooltip: {
      colorBgSpotlight: darkColors.surfaceTertiary,
      colorTextLightSolid: darkColors.text,
    },

    Modal: {
      colorBgElevated: darkColors.bgElevated,
      headerBg: darkColors.bgElevated,
      titleColor: darkColors.text,
      colorBorderSecondary: darkColors.border,
      footerBg: darkColors.bgElevated,
      borderRadiusLG: 12,
    },

    Drawer: {
      colorBgElevated: darkColors.bgElevated,
      colorText: darkColors.text,
      colorBorder: darkColors.border,
    },

    Alert: {
      colorBgContainer: darkColors.bgElevated,
      colorSuccessBg: 'rgba(16, 185, 129, 0.12)',
      colorSuccessBorder: 'rgba(16, 185, 129, 0.35)',
      colorErrorBg: 'rgba(239, 68, 68, 0.12)',
      colorErrorBorder: 'rgba(239, 68, 68, 0.35)',
      colorWarningBg: 'rgba(245, 158, 11, 0.12)',
      colorWarningBorder: 'rgba(245, 158, 11, 0.35)',
      colorInfoBg: 'rgba(59, 130, 246, 0.12)',
      colorInfoBorder: 'rgba(59, 130, 246, 0.35)',
    },

    Progress: {
      remainingColor: darkColors.surfaceTertiary,
    },

    Steps: {
      colorTextDescription: darkColors.textSec,
      colorPrimary: darkColors.primary,
      colorSplit: darkColors.border,
    },

    Descriptions: {
      labelBg: darkColors.bgSunken,
      colonMarginRight: 8,
      colonMarginLeft: 8,
    },

    Result: {
      titleFontSize: 20,
      subtitleFontSize: 14,
    },

    Empty: {
      colorTextDisabled: darkColors.textDisabled,
    },

    Avatar: {
      colorBgBase: darkColors.selected,
    },

    Statistic: {
      contentFontSize: 24,
    },

    Form: {
      labelColor: darkColors.textSec,
      labelFontSize: 13,
      verticalLabelPadding: 0,
    },
  },
};

export const lightThemeConfig: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    // Global look & feel
    colorPrimary: lightColors.primary,
    colorSuccess: lightColors.success,
    colorWarning: lightColors.warning,
    colorError: lightColors.error,
    colorInfo: lightColors.info,

    colorBgBase: lightColors.bgBase,
    colorBgContainer: lightColors.bgElevated,
    colorBgElevated: lightColors.bgElevated,
    colorBorder: lightColors.border,
    colorSplit: lightColors.border,

    colorTextBase: lightColors.text,
    colorText: lightColors.text,
    colorTextSecondary: lightColors.textSec,
    colorTextTertiary: lightColors.textTertiary,
    colorTextQuaternary: lightColors.textDisabled,

    controlItemBgHover: lightColors.hover,
    controlItemBgActive: lightColors.selected,

    // Border radius (modern 8px default)
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,

    // Control heights (44px minimum for accessibility)
    controlHeight: COMPONENT_SIZE.controlHeight,
    controlHeightLG: COMPONENT_SIZE.controlHeightLG,
    controlHeightSM: COMPONENT_SIZE.controlHeightSM,

    // Padding
    padding: 16,
    paddingSM: 12,
    paddingLG: 24,

    // Typography
    fontSize: 14,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

    // Shadows (simplified per new spec)
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
    boxShadowSecondary: '0 1px 3px rgba(0, 0, 0, 0.08)',

    colorLink: lightColors.primary,
    colorLinkHover: lightColors.primaryHover,
    colorLinkActive: lightColors.primaryActive,

    // Pin antd popups/modals above the sticky header (--z-sticky = 1020).
    zIndexPopupBase: Z_INDEX.zIndexModal,
  },

  components: {
    Layout: {
      headerBg: '#FFFFFF',
      siderBg: lightColors.bgElevated,
      bodyBg: lightColors.bgBase,
      headerHeight: 64,
    },

    Card: {
      colorBgContainer: lightColors.bgElevated,
      boxShadowTertiary: lightColors.shadowCard,
      headerHeight: 52,
      padding: 20,
      borderRadiusLG: 12,
    },

    Button: {
      borderRadius: 8,
      controlHeight: 44,
      paddingInline: 24,
      colorPrimary: lightColors.primary,
      colorPrimaryHover: lightColors.primaryHover,
      colorPrimaryActive: lightColors.primaryActive,
      primaryShadow: lightColors.shadowButtonPrimary,
      defaultColor: lightColors.text,
      defaultBg: '#FFFFFF',
      defaultBorderColor: lightColors.border,
      defaultHoverBg: '#FFFFFF',
      defaultActiveBg: lightColors.selected,
      defaultShadow: lightColors.shadowSubtle,
      ghostBg: 'transparent',
    },

    Input: {
      controlHeight: 44,
      colorBgContainer: '#FFFFFF',
      colorTextPlaceholder: lightColors.textTertiary,
      colorBorder: lightColors.border,
      activeBorderColor: lightColors.primary,
      activeShadow: lightColors.shadowFocus,
      hoverBorderColor: lightColors.primary,
      borderRadius: 8,
      paddingInline: 16,
      addonBg: lightColors.bgSunken,
    },

    Select: {
      controlHeight: 44,
      colorBgContainer: '#FFFFFF',
      colorBorder: lightColors.border,
      activeBorderColor: lightColors.primary,
      colorTextPlaceholder: lightColors.textTertiary,
      optionSelectedBg: lightColors.selected,
      optionActiveBg: '#FFFFFF',
      borderRadius: 8,
    },

    DatePicker: {
      controlHeight: 44,
      colorBgContainer: '#FFFFFF',
      colorBorder: lightColors.border,
      activeBorderColor: lightColors.primary,
      cellHoverWithRangeBg: lightColors.selected,
      colorBgElevated: lightColors.bgElevated,
    },

    Checkbox: {
      borderRadiusSM: 6,
      colorBgContainer: '#FFFFFF',
      colorBorder: lightColors.border,
      colorPrimary: lightColors.primary,
    },

    Radio: {
      colorPrimary: lightColors.primary,
      buttonCheckedBg: lightColors.selected,
      buttonSolidCheckedBg: lightColors.primary,
    },

    Switch: {
      colorPrimary: lightColors.primary,
      colorBgContainer: lightColors.bgSunken,
      trackHeight: 22,
      handleSize: 18,
      borderRadiusSM: 20,
    },

    Tag: {
      colorText: lightColors.text,
      defaultBg: lightColors.selected,
      borderRadiusSM: 6,
      colorSuccess: lightColors.success,
      colorError: lightColors.error,
      colorWarning: lightColors.warning,
      colorInfo: lightColors.info,
    },

    Badge: {
      colorBorderBg: lightColors.bgElevated,
      colorSuccess: lightColors.success,
      colorError: lightColors.error,
      colorWarning: lightColors.warning,
      colorInfo: lightColors.info,
    },

    Table: {
      colorBgContainer: lightColors.bgElevated,
      colorFillAlter: lightColors.surfaceTertiary,
      headerBg: lightColors.bgElevated,
      headerColor: lightColors.textSec,
      borderColor: lightColors.border,
      rowHoverBg: '#FFFFFF',
      rowSelectedBg: lightColors.selected,
      rowSelectedHoverBg: lightColors.selected,
      stickyScrollBarBg: lightColors.bgSunken,
      stickyScrollBarBorderRadius: 6,
      cellPaddingBlock: 16,
      cellPaddingInline: 12,
      fontSize: 14,
    },

    Pagination: {
      itemActiveBg: lightColors.selected,
      colorText: lightColors.text,
      colorBgContainer: '#FFFFFF',
      colorBorder: lightColors.border,
    },

    Tabs: {
      itemActiveColor: lightColors.text,
      itemSelectedColor: lightColors.text,
      itemHoverColor: lightColors.textSec,
      inkBarColor: lightColors.primary,
      cardBg: lightColors.bgElevated,
      titleFontSizeLG: 16,
    },

    Menu: {
      darkItemBg: lightColors.bgElevated,
      darkSubMenuItemBg: lightColors.bgSunken,
      darkItemSelectedBg: lightColors.selected,
      darkItemColor: lightColors.textSec,
      darkItemSelectedColor: lightColors.text,
      itemBorderRadius: 8,
      itemBg: lightColors.bgElevated,
      subMenuItemBg: lightColors.bgSunken,
      itemSelectedBg: lightColors.selected,
      itemColor: lightColors.textSec,
      itemSelectedColor: lightColors.text,
    },

    Dropdown: {
      colorBgElevated: '#FFFFFF',
      controlItemBgHover: lightColors.surfaceTertiary,
      controlItemBgActive: lightColors.selected,
      colorBorder: lightColors.border,
      boxShadowSecondary: lightColors.shadowCard,
    },

    Popover: {
      colorBgElevated: '#FFFFFF',
      colorText: lightColors.text,
      colorBorder: lightColors.border,
    },

    Tooltip: {
      colorBgSpotlight: '#262626',
      colorTextLightSolid: '#ffffff',
    },

    Modal: {
      colorBgElevated: lightColors.bgElevated,
      headerBg: lightColors.bgElevated,
      titleColor: lightColors.text,
      colorBorderSecondary: lightColors.border,
      footerBg: lightColors.bgElevated,
      borderRadiusLG: 12,
    },

    Drawer: {
      colorBgElevated: lightColors.bgElevated,
      colorText: lightColors.text,
      colorBorder: lightColors.border,
    },

    Alert: {
      colorBgContainer: lightColors.bgElevated,
      colorSuccessBg: 'rgba(5,150,105,0.06)',
      colorSuccessBorder: 'rgba(5,150,105,0.3)',
      colorErrorBg: '#FEE2E2',
      colorErrorBorder: 'rgba(220,38,38,0.3)',
      colorWarningBg: 'rgba(217,119,6,0.06)',
      colorWarningBorder: 'rgba(217,119,6,0.3)',
      colorInfoBg: '#DBEAFE',
      colorInfoBorder: 'rgba(37,99,235,0.3)',
    },

    Progress: {
      remainingColor: lightColors.bgSunken,
    },

    Steps: {
      colorTextDescription: lightColors.textSec,
      colorPrimary: lightColors.primary,
      colorSplit: lightColors.border,
    },

    Descriptions: {
      labelBg: lightColors.bgSunken,
      colonMarginRight: 8,
      colonMarginLeft: 8,
    },

    Result: {
      titleFontSize: 20,
      subtitleFontSize: 14,
    },

    Empty: {
      colorTextDisabled: lightColors.textDisabled,
    },

    Avatar: {
      colorBgBase: lightColors.selected,
    },

    Statistic: {
      contentFontSize: 24,
    },

    Form: {
      labelColor: lightColors.textSec,
      labelFontSize: 13,
      verticalLabelPadding: 0,
    },
  },
};

export const buildStyledTheme = (
  isDark: boolean
): {
  mode: 'dark' | 'light';
  isDark: boolean;
  colors: {
    bgBase: string;
    bgElevated: string;
    bgControl: string;
    bgSunken: string;
    surfaceSecondary: string;
    surfaceTertiary: string;
    // Shell tokens
    shellBg: string;
    shellTextPrimary: string;
    shellTextMuted: string;
    shellBorder: string;
    shellHover: string;
    shellSelected: string;
    shellText: string;
    shellTextHover: string;
    shellTextSelected: string;
    // Sub-sidebar tokens
    subSidebarBg: string;
    subSidebarBorder: string;
    // Viewer tokens
    viewerBg: string;
    filmstripBg: string;
    // Text
    text: string;
    textSec: string;
    textTertiary: string;
    textDisabled: string;
    primary: string;
    primaryHover: string;
    primaryActive: string;
    border: string;
    hover: string;
    selected: string;
    success: string;
    warning: string;
    error: string;
    info: string;
    aiAccent: string;
    aiGlow: string;
    accentGradient: string;
    findingBg: string;
  };
  glass: {
    bg: string;
    border: string;
    shadow: string;
  };
  spacing: (factor: number) => string;
  shadows: {
    sm: string;
    md: string;
    lg: string;
    subtle: string;
    card: string;
    elevated: string;
    focus: string;
  };
} => {
  const baseColors = isDark ? darkColors : lightColors;

  const spacing = (factor: number) => `${factor * 4}px`;

  const shadows = isDark
    ? {
        sm: '0 1px 2px rgba(0,0,0,0.15)',
        md: '0 4px 6px rgba(0,0,0,0.25)',
        lg: '0 10px 15px rgba(0,0,0,0.35)',
        subtle: baseColors.shadowSubtle,
        card: baseColors.shadowCard,
        elevated: baseColors.shadowElevated,
        focus: baseColors.shadowFocus,
      }
    : {
        sm: '0 1px 3px rgba(0, 0, 0, 0.08)',
        md: '0 2px 4px rgba(0, 0, 0, 0.12)',
        lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
        subtle: baseColors.shadowSubtle,
        card: baseColors.shadowCard,
        elevated: '0 4px 16px rgba(0, 0, 0, 0.15)',
        focus: baseColors.shadowFocus,
      };

  return {
    mode: isDark ? 'dark' : 'light',
    isDark,

    colors: {
      bgBase: baseColors.bgBase,
      bgElevated: baseColors.bgElevated,
      bgControl: baseColors.bgControl,
      bgSunken: baseColors.bgSunken,
      surfaceSecondary: baseColors.surfaceSecondary,
      surfaceTertiary: baseColors.surfaceTertiary,
      // Shell tokens
      shellBg: baseColors.shellBg,
      shellTextPrimary: baseColors.shellTextPrimary,
      shellTextMuted: baseColors.shellTextMuted,
      shellBorder: baseColors.shellBorder,
      shellHover: baseColors.shellHover,
      shellSelected: baseColors.shellSelected,
      shellText: baseColors.shellText,
      shellTextHover: baseColors.shellTextHover,
      shellTextSelected: baseColors.shellTextSelected,
      // Sub-sidebar tokens
      subSidebarBg: baseColors.subSidebarBg,
      subSidebarBorder: baseColors.subSidebarBorder,
      // Viewer tokens
      viewerBg: baseColors.viewerBg,
      filmstripBg: baseColors.filmstripBg,
      // Text
      text: baseColors.text,
      textSec: baseColors.textSec,
      textTertiary: baseColors.textTertiary,
      textDisabled: baseColors.textDisabled,
      primary: baseColors.primary,
      primaryHover: baseColors.primaryHover,
      primaryActive: baseColors.primaryActive,
      border: baseColors.border,
      hover: baseColors.hover,
      selected: baseColors.selected,
      success: baseColors.success,
      warning: baseColors.warning,
      error: baseColors.error,
      info: baseColors.info,
      aiAccent: baseColors.aiAccent,
      aiGlow: baseColors.aiGlow,
      accentGradient: baseColors.accentGradient,
      findingBg: baseColors.findingBg,
    },

    glass: {
      bg: baseColors.glassBg,
      border: baseColors.glassBorder,
      shadow: baseColors.glassShadow,
    },

    spacing,
    shadows,
  };
};
